import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { getBackendURL } from "./env.js";
import { getSupabase } from "./supabaseClient";
import MeetingBookingTab from "./MeetingBookingTab";

const MAX_FILES = 5;
const MAX_TOTAL_BYTES = 25 * 1024 * 1024;
const MAX_ATTACHMENT_MB = 10; // must match MAX_ATTACHMENT_MB in backend/api/bug_reports.py

const formatBytes = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const isImageOrVideo = (file) => {
  const t = (file.type || "").toLowerCase();
  return t.startsWith("image/") || t.startsWith("video/");
};

/**
 * Help Desk body — two tabs:
 *   1. Send us a message or report a bug — POSTs /api/bug-report/
 *   2. Book a meeting — calendar + slot picker (MeetingBookingTab)
 *
 * Shared by HelpDeskButton (renders this inside a Modal) and HelpDeskPage
 * (renders it as a standalone page). Pass `onSubmitted` to be notified
 * after a successful message-tab submit; the modal uses that to close
 * itself. The page can omit it.
 *
 * `isActive` controls whether the auth check re-runs (the modal sets this
 * to `isOpen` so the check fires each time the modal opens; the page
 * leaves it true so it runs once on mount).
 */
const HelpDeskContent = ({ isActive = true, onSubmitted }) => {
  const toast = useToast();
  const fileInputRef = useRef(null);

  const [messageType, setMessageType] = useState("message");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [sessionEmail, setSessionEmail] = useState("");
  const [sessionName, setSessionName] = useState("");
  const [sessionRole, setSessionRole] = useState("");
  const [manualUsername, setManualUsername] = useState("");
  const [manualEmail, setManualEmail] = useState("");
  const [manualRole, setManualRole] = useState("");

  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);

  const resetForm = useCallback(() => {
    setMessageType("message");
    setTitle("");
    setDescription("");
    setFiles([]);
    setManualUsername("");
    setManualEmail("");
    setManualRole("");
  }, []);

  useEffect(() => {
    if (!isActive) return undefined;
    let cancelled = false;
    setAuthChecked(false);
    (async () => {
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (cancelled) return;
        const signedIn = Boolean(session?.user);
        setIsSignedIn(signedIn);
        if (signedIn) {
          const email = session.user.email || "";
          let nm =
            session.user.user_metadata?.full_name ||
            session.user.user_metadata?.username ||
            "";
          let rl = "";
          if (email) {
            try {
              const { data: row } = await supabase
                .from("users")
                .select("name, role")
                .eq("email", email)
                .maybeSingle();
              if (row) {
                nm = row.name || nm;
                rl = row.role || "";
              }
            } catch (_) { /* ignore */ }
          }
          if (cancelled) return;
          setSessionEmail(email);
          setSessionName(nm);
          setSessionRole(rl);
        } else {
          setSessionEmail("");
          setSessionName("");
          setSessionRole("");
        }
      } catch (err) {
        console.warn("[HelpDesk] auth check failed", err);
        if (cancelled) return;
        setIsSignedIn(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isActive]);

  const handleAddFiles = useCallback(
    (incoming) => {
      const arr = Array.from(incoming || []);
      if (!arr.length) return;

      const accepted = [];
      const rejected = [];

      for (const f of arr) {
        if (!isImageOrVideo(f)) {
          rejected.push(`${f.name}: only image/video files allowed`);
          continue;
        }
        accepted.push(f);
      }

      const merged = [...files, ...accepted];
      const trimmed = merged.slice(0, MAX_FILES);
      const droppedForCount = merged.length - trimmed.length;

      let runningTotal = 0;
      const final = [];
      for (const f of trimmed) {
        if (f.size > MAX_ATTACHMENT_MB * 1024 * 1024) {
          rejected.push(`${f.name}: exceeds ${MAX_ATTACHMENT_MB} MB per attachment`);
          continue;
        }
        if (runningTotal + f.size > MAX_TOTAL_BYTES) {
          rejected.push(`${f.name}: would exceed ${MAX_TOTAL_BYTES / (1024 * 1024)} MB total`);
          continue;
        }
        runningTotal += f.size;
        final.push(f);
      }

      setFiles(final);

      if (droppedForCount > 0) {
        rejected.push(`Only the first ${MAX_FILES} attachments are kept.`);
      }
      if (rejected.length) {
        toast({
          title: "Some files were skipped",
          description: rejected.join("\n"),
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [files, toast],
  );

  const handleRemoveFile = useCallback((idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleSubmit = useCallback(async () => {
    const t = title.trim();
    const d = description.trim();
    if (!t) {
      toast({ title: "Title is required", status: "error", duration: 3000 });
      return;
    }
    if (!d) {
      toast({ title: "Description is required", status: "error", duration: 3000 });
      return;
    }
    const manualUsernameTrimmed = manualUsername.trim();
    const manualEmailTrimmed = manualEmail.trim();
    const manualRoleTrimmed = manualRole.trim();
    if (authChecked && !isSignedIn) {
      if (!manualUsernameTrimmed) {
        toast({
          title: "Username is required",
          description: "If you don't have an account yet, enter N/A.",
          status: "error",
          duration: 4000,
        });
        return;
      }
      if (!manualEmailTrimmed) {
        toast({
          title: "Email is required",
          description: "If you don't have an account yet, enter N/A.",
          status: "error",
          duration: 4000,
        });
        return;
      }
      if (!manualRoleTrimmed) {
        toast({
          title: "Role is required",
          description: "If you don't have an account yet, enter N/A.",
          status: "error",
          duration: 4000,
        });
        return;
      }
    }

    setSubmitting(true);
    try {
      let userEmail = "";
      let userName = "";
      let userRole = "";
      try {
        const supabase = await getSupabase();
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user) {
          userEmail = session.user.email || "";
          userName =
            session.user.user_metadata?.full_name ||
            session.user.user_metadata?.username ||
            "";
          if (userEmail) {
            const { data: row } = await supabase
              .from("users")
              .select("name, role")
              .eq("email", userEmail)
              .maybeSingle();
            if (row) {
              userName = row.name || userName;
              userRole = row.role || "";
            }
          }
        } else {
          userName = manualUsernameTrimmed;
          userEmail = manualEmailTrimmed;
          userRole = manualRoleTrimmed;
        }
      } catch (err) {
        console.warn("[HelpDesk] failed to read user context", err);
        userName = manualUsernameTrimmed;
        userEmail = manualEmailTrimmed;
        userRole = manualRoleTrimmed;
      }

      const fd = new FormData();
      fd.append("message_type", messageType);
      fd.append("title", t);
      fd.append("description", d);
      fd.append("user_name", userName);
      fd.append("user_email", userEmail);
      fd.append("user_role", userRole);
      for (const f of files) fd.append("attachments", f);

      const res = await fetch(`${getBackendURL()}/api/bug-report/`, {
        method: "POST",
        body: fd,
        credentials: "include",
        headers: {
          "X-CSRFToken":
            document.cookie.split("csrftoken=")[1]?.split(";")[0] || "",
        },
      });

      if (!res.ok) {
        let msg = `Request failed (${res.status})`;
        try {
          const j = await res.json();
          if (j?.error) msg = j.error;
        } catch (_) {
          /* ignore */
        }
        throw new Error(msg);
      }

      toast({
        title: "Message sent",
        description: "Thanks — we'll take a look.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
      resetForm();
      if (onSubmitted) onSubmitted();
    } catch (err) {
      toast({
        title: "Could not send your message",
        description: err?.message || "Unknown error",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    messageType,
    title,
    description,
    files,
    manualUsername,
    manualEmail,
    manualRole,
    authChecked,
    isSignedIn,
    toast,
    onSubmitted,
    resetForm,
  ]);

  return (
    <Tabs variant="enclosed" colorScheme="red" isLazy>
      <TabList>
        <Tab>Send us a message or report a bug</Tab>
        <Tab>Book a meeting</Tab>
      </TabList>
      <TabPanels>
        <TabPanel px={0}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>What kind of message is this?</FormLabel>
              <RadioGroup
                value={messageType}
                onChange={setMessageType}
                isDisabled={submitting}
              >
                <HStack spacing={6}>
                  <Radio value="message">Send a message</Radio>
                  <Radio value="bug">Report a bug</Radio>
                </HStack>
              </RadioGroup>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                placeholder="Short summary of the issue or message"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={200}
                isDisabled={submitting}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="What happened, or what would you like to tell us?"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={6}
                isDisabled={submitting}
              />
            </FormControl>

            {authChecked && !isSignedIn && (
              <>
                <FormControl isRequired>
                  <FormLabel>Username</FormLabel>
                  <Input
                    placeholder="Your username (or N/A if you don't have an account)"
                    value={manualUsername}
                    onChange={(e) => setManualUsername(e.target.value)}
                    maxLength={200}
                    isDisabled={submitting}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="your@email.com (or N/A)"
                    value={manualEmail}
                    onChange={(e) => setManualEmail(e.target.value)}
                    maxLength={200}
                    isDisabled={submitting}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Role</FormLabel>
                  <Input
                    placeholder="e.g. Doctor, Student, Admin (or N/A)"
                    value={manualRole}
                    onChange={(e) => setManualRole(e.target.value)}
                    maxLength={100}
                    isDisabled={submitting}
                  />
                </FormControl>
              </>
            )}

            <FormControl>
              <FormLabel>
                Attachments (optional) — images or videos, up to {MAX_FILES} files /{" "}
                {MAX_ATTACHMENT_MB} MB each / {MAX_TOTAL_BYTES / (1024 * 1024)} MB total
              </FormLabel>
              <Input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*"
                onChange={(e) => {
                  handleAddFiles(e.target.files);
                  e.target.value = "";
                }}
                isDisabled={submitting}
                p={1}
              />
              {files.length > 0 && (
                <VStack mt={2} spacing={1} align="stretch">
                  {files.map((f, idx) => (
                    <HStack
                      key={`${f.name}-${idx}`}
                      justify="space-between"
                      bg="gray.50"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      <Text fontSize="sm" noOfLines={1} title={f.name}>
                        {f.name}
                      </Text>
                      <HStack spacing={2} flexShrink={0}>
                        <Text fontSize="xs" color="gray.600">
                          {formatBytes(f.size)}
                        </Text>
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label={`Remove ${f.name}`}
                          icon={<CloseIcon boxSize={2.5} />}
                          onClick={() => handleRemoveFile(idx)}
                          isDisabled={submitting}
                        />
                      </HStack>
                    </HStack>
                  ))}
                  <Text fontSize="xs" color="gray.600">
                    Total: {formatBytes(totalBytes)} / {formatBytes(MAX_TOTAL_BYTES)}
                  </Text>
                </VStack>
              )}
            </FormControl>

            <Button
              colorScheme="red"
              onClick={handleSubmit}
              isLoading={submitting}
              loadingText="Sending"
            >
              Submit
            </Button>
          </VStack>
        </TabPanel>

        <TabPanel px={0}>
          <MeetingBookingTab
            isSignedIn={isSignedIn}
            authChecked={authChecked}
            prefillEmail={sessionEmail}
            prefillName={sessionName}
            prefillRole={sessionRole}
            onClose={onSubmitted}
          />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default HelpDeskContent;
